import { useCallback, useContext, useEffect, useState } from 'react';
import './Header.css';

import { Select } from 'antd';
import { useAuth } from 'src/Contexts/AuthContext';
import { useInstallationsQuery } from 'src/queries';
import { ThemeContext } from 'src/Contexts/ThemeContext';
import { useSelectedInstallation } from 'src/Contexts/SelectedInstallationContext';
import { measurementsService } from 'src/api/services';
import { parseUtcTimestamp } from 'src/utils/timestamps';

import { FaTimes } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa';

import { useTranslation } from 'react-i18next';

const { Option } = Select;

const Header = () => {
  const { t } = useTranslation();
  const { DarkTheme } = useContext(ThemeContext);
  const { currentUser } = useAuth();
  const { data: installations = [] } = useInstallationsQuery();
  const { selectedInstallationID, setSelectedInstallationID } = useSelectedInstallation();
  const [systemStatus, setSystemStatus] = useState('...Loading'); // Default to LOADING

  const fetchLatestTime = useCallback(async () => {
    if (!currentUser || !currentUser.token) {
      if (import.meta.env.DEV) {
        console.error('No user token available for fetching the latest times.');
      }
      setSystemStatus('OFFLINE'); // Assume OFFLINE if no token is available
      return;
    }

    if (!selectedInstallationID) {
      setSystemStatus('OFFLINE');
      return;
    }

    const installationObject = installations.find((inst) => inst.id === selectedInstallationID);
    if (!installationObject) {
      if (import.meta.env.DEV) {
        console.error('Installation details not found.');
      }
      setSystemStatus('OFFLINE');
      return;
    }

    try {
      const data = await measurementsService.getLatest(selectedInstallationID);

      if (!data || !data.measurement_timestamp) {
        setSystemStatus('OFFLINE');
        return;
      }

      // Backend timestamps are naive UTC — parse them as such, or the online
      // check is skewed by the local UTC offset.
      const latestTime = parseUtcTimestamp(data.measurement_timestamp);
      if (!latestTime) {
        setSystemStatus('OFFLINE');
        return;
      }

      const now = new Date();
      // deadman_threshold is in HOURS everywhere (management form writes
      // hours, backend alert evaluator reads hours; default 24 matches the
      // evaluator's DEFAULT_DEADMAN_HOURS).
      const diffHours = (now.getTime() - latestTime.getTime()) / (1000 * 60 * 60);
      const deadmanThresholdHours =
        installationObject.configuration?.deadman_threshold ||
        installationObject.deadman_threshold ||
        24;

      if (diffHours > deadmanThresholdHours) {
        setSystemStatus('OFFLINE');
      } else {
        setSystemStatus('ONLINE');
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to fetch latest times:', error);
      }
      setSystemStatus('OFFLINE');
    }
  }, [currentUser, installations, selectedInstallationID]);

  useEffect(() => {
    if (installations.length > 0 && !selectedInstallationID) {
      setSelectedInstallationID(installations[0].id);
    }
    if (selectedInstallationID) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void fetchLatestTime();
    }
    // setSelectedInstallationID is stable from context, fetchLatestTime is memoized
  }, [installations, selectedInstallationID, fetchLatestTime, setSelectedInstallationID]);

  const handleChange = (value) => {
    setSelectedInstallationID(value);
  };

  const sortedInstallations = installations.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <header className={`${DarkTheme ? 'dark' : ''}`}>
      <div className="dropdown-menu">
        <Select
          value={
            selectedInstallationID ||
            (sortedInstallations.length > 0 ? sortedInstallations[0].id : undefined)
          }
          onChange={handleChange}
          style={{ width: '75.5%' }}
          loading={sortedInstallations.length === 0}
        >
          {sortedInstallations.map((installation) => (
            <Option key={installation.id} value={installation.id}>
              {installation.name}
            </Option>
          ))}
        </Select>
      </div>

      <div className="tools">
        <p style={{ fontWeight: '700', color: '#201c1c' }}>
          {t('System Status')}: {systemStatus}
        </p>
        {systemStatus === 'ONLINE' ? (
          <FaCheck style={{ color: 'green' }} />
        ) : (
          <FaTimes style={{ color: 'red' }} />
        )}
      </div>
    </header>
  );
};

export default Header;
